/**
 * useLivingConversation — CTO Directive #111.
 *
 * Single source of truth for the Living Orb. Polls the living
 * cursor endpoint (`/api/me/voice/stream/live`) with the timestamp
 * of the last event we saw and hydrates the local cache with:
 *
 *   * ``state``         — the real ConversationState from the server
 *                         (listening / thinking / waiting / speaking).
 *   * ``profile``       — the live VoiceProfile facets so the Orb
 *                         breathes at the tempo / warmth / energy
 *                         the server has just decided on.
 *   * ``events``        — the most recent events, in chronological
 *                         order (bounded).
 *   * ``lastEvent``     — the newest event kind so callers can react
 *                         to one-shot moments (e.g. interruption).
 *
 * There is no "polling" concept in the UI vocabulary: React state
 * is a cache; the server is truth. When another device speaks, the
 * Orb here reflects it within one tick.
 */
import { useEffect, useRef, useState } from "react";

import { api } from "@/lib/api";

const DEFAULT_INTERVAL_MS = 900;

export function useLivingConversation({
  enabled = true,
  intervalMs = DEFAULT_INTERVAL_MS,
  maxEvents = 40,
} = {}) {
  const [state, setState] = useState("listening");
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [currentMicrophone, setCurrentMicrophone] = useState(null);
  const cursorRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return () => {};
    cancelledRef.current = false;

    const applySnapshot = (data) => {
      if (cancelledRef.current || !data) return;
      if (data.state) setState(data.state);
      if (data.voice_profile) setProfile(data.voice_profile);
      if (typeof data.current_speaker !== "undefined") {
        setCurrentSpeaker(data.current_speaker || null);
      }
      if (typeof data.current_microphone !== "undefined") {
        setCurrentMicrophone(data.current_microphone || null);
      }
      const incoming = Array.isArray(data.events) ? data.events : [];
      if (incoming.length) {
        setEvents((prev) => {
          const merged = [...prev, ...incoming];
          return merged.slice(-maxEvents);
        });
        const newest = incoming[incoming.length - 1];
        setLastEvent(newest);
        if (data.cursor) cursorRef.current = data.cursor;
        else if (newest.at) cursorRef.current = newest.at;
      } else if (data.cursor) {
        cursorRef.current = data.cursor;
      }
    };

    const tick = async () => {
      try {
        const params = cursorRef.current
          ? { since: cursorRef.current }
          : { limit: maxEvents };
        const r = await api.get("/me/voice/stream/live", { params });
        applySnapshot(r.data);
      } catch {
        // Silent — the Orb stays alive even when the network hiccups.
      }
    };

    tick();
    const id = setInterval(tick, Math.max(300, intervalMs));
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, [enabled, intervalMs, maxEvents]);

  return {
    state,
    profile,
    events,
    lastEvent,
    currentSpeaker,
    currentMicrophone,
  };
}
